import { openCookieSettings } from "@/components/CookieConsent";
import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { href: "/#club", label: "The club", id: "club" },
  { href: "/#experiences", label: "Experiences", id: "experiences" },
  { href: "/#how", label: "How it works", id: "how" },
];

const AccountActions = ({ user, variant, onNavigate }) => {
  if (user && user.status === "approved") return <Link to="/connect" onClick={onNavigate} className="btn-primary min-h-[44px] px-5" data-testid={`${variant}-members-area-link`}>Members' area</Link>;
  if (user) return <Link to="/status" onClick={onNavigate} className="btn-outline min-h-[44px] border-primary px-5 text-primary" data-testid={`${variant}-application-status-link`}>Application status</Link>;
  return (
    <>
      <Link to="/signin" onClick={onNavigate} className="min-h-[44px] content-center px-2 text-[15px] font-medium hover:text-primary" data-testid={`${variant}-signin-link`}>Sign in</Link>
      <Link to="/apply" onClick={onNavigate} className="btn-outline min-h-[44px] border-primary px-5 text-primary" data-testid={`${variant}-apply-link`}>Apply for membership</Link>
    </>
  );
};

export const PublicHeader = ({ minimal = false }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-xl" data-testid="public-header">
      <div className="shell flex h-[74px] items-center justify-between gap-6">
        <Logo />
        <nav aria-label="Main" className="hidden items-center gap-12 lg:flex">
          {LINKS.map((l) => (
            <a key={l.id} href={l.href} className="text-[15px] text-foreground/90 transition-colors hover:text-primary" data-testid={`nav-${l.id}`}>{l.label}</a>
          ))}
        </nav>
        <div className="hidden items-center gap-5 lg:flex">
          {minimal && !user ? (
            <p className="text-[15px]">Already a member? <Link to="/signin" className="ml-2 font-semibold text-primary underline underline-offset-4" data-testid="header-signin-link">Sign in</Link></p>
          ) : <AccountActions user={user} variant="header" />}
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button type="button" className="flex h-11 w-11 items-center justify-center rounded-lg lg:hidden" aria-label="Open menu" data-testid="mobile-menu-button">
              <Menu className="h-6 w-6" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[86vw] max-w-sm border-border bg-background">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SheetDescription className="sr-only">Site navigation and membership links</SheetDescription>
            <nav aria-label="Mobile" className="mt-10 flex flex-col gap-2">
              {LINKS.map((l) => (
                <a key={l.id} href={l.href} onClick={() => setOpen(false)} className="font-display flex min-h-[52px] items-center border-b border-border text-3xl" data-testid={`mobile-nav-${l.id}`}>{l.label}</a>
              ))}
              <div className="mt-6 flex flex-col gap-3"><AccountActions user={user} variant="mobile" onNavigate={() => setOpen(false)} /></div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-border" data-testid="site-footer">
    <div className="shell flex flex-col gap-10 py-14 md:flex-row md:items-end md:justify-between">
      <div className="space-y-4">
        <Logo testId="footer-logo" />
        <p className="max-w-sm text-sm text-muted-foreground">A private members club for approved adults to find their circle and co-host exceptional experiences.</p>
        <p className="text-sm text-muted-foreground">www.GenesisMembersClub.com</p>
      </div>
      <nav aria-label="Footer" className="flex flex-wrap gap-x-8 gap-y-3 text-sm">
        <a href="/#club" className="hover:text-primary" data-testid="footer-club-link">The club</a>
        <a href="/#how" className="hover:text-primary" data-testid="footer-how-link">How it works</a>
        <NavLink to="/contact" className="hover:text-primary" data-testid="footer-contact-link">Contact</NavLink>
        <NavLink to="/privacy" className="hover:text-primary" data-testid="footer-privacy-link">Privacy</NavLink>
        <NavLink to="/terms" className="hover:text-primary" data-testid="footer-terms-link">Terms</NavLink>
        <button type="button" onClick={openCookieSettings} className="hover:text-primary" data-testid="footer-cookie-settings">Cookie settings</button>
      </nav>
    </div>
    <div className="shell border-t border-border py-6 text-xs text-muted-foreground">
      © {new Date().getFullYear()} Genesis Members Club. Planning only. Bookings and payments are arranged outside the platform.
    </div>
  </footer>
);
